import React, { useState, useEffect } from 'react';
import styles from './date-range.module.scss';
import { ToggleButtonGroup, ToggleOption } from '../toggle-button-group';
import { DateRangePickerProps, DateRange } from './daterange-picker.types';

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  title = 'Date Filter',
  subtitle = 'Filter results by specific time periods',
  presets = [],
  onApply,
  onClose,
  isOpen,
  initialFrom,
  initialTo,
  showCustomOption = true,
}) => {
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      // Set initial values when modal opens
      if (initialFrom) {
        setCustomFrom(formatDateForInput(initialFrom));
      }
      if (initialTo) {
        setCustomTo(formatDateForInput(initialTo));
      }
      
      // Default to first preset if available
      if (presets.length > 0 && !selectedOption) {
        setSelectedOption(presets[0].label);
      } else if (showCustomOption && !selectedOption) {
        setSelectedOption('custom');
      }
    }
  }, [isOpen, initialFrom, initialTo, presets, selectedOption, showCustomOption]);

  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric' 
    };
    return date.toLocaleDateString('en-US', options);
  };

  const handleApply = () => {
    let from: Date;
    let to: Date;
    let type: 'preset' | 'custom';
    let presetLabel: string | undefined;

    if (selectedOption === 'custom') {
      if (!customFrom || !customTo) {
        alert('Please select both From and To dates');
        return;
      }
      from = new Date(customFrom);
      to = new Date(customTo);
      type = 'custom';
    } else {
      // Find the preset
      const preset = presets.find(p => p.label === selectedOption);
      if (!preset) return;

      const range = preset.getValue();
      from = range.from;
      to = range.to;
      type = 'preset';
      presetLabel = preset.label;
    }

    onApply({ from, to, type, presetLabel });
    onClose();
  };

  const handleCustomFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomFrom(e.target.value);
  };

  const handleCustomToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTo(e.target.value);
  };

  // Build toggle options
  const toggleOptions: ToggleOption[] = [
    ...presets.map(preset => ({
      value: preset.label,
      label: preset.label,
    })),
  ];

  if (showCustomOption) {
    toggleOptions.push({
      value: 'custom',
      label: 'Select Range',
      expandOnSelect: true,
      customContent: (
        <div className={styles.customDateInputs}>
          <div className={styles.dateInputGroup}>
            <label className={styles.inputLabel}>From</label>
            <div className={styles.inputWrapper}>
              <input
                type="date"
                value={customFrom}
                onChange={handleCustomFromChange}
                className={styles.dateInput}
              />
              <svg className={styles.calendarIcon} width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M6.66667 1.66667V4.16667M13.3333 1.66667V4.16667M2.5 16.6667V6.66667C2.5 6.00363 2.76339 5.36774 3.23223 4.8989C3.70107 4.43006 4.33696 4.16667 5 4.16667H15C15.663 4.16667 16.2989 4.43006 16.7678 4.8989C17.2366 5.36774 17.5 6.00363 17.5 6.66667V16.6667M2.5 16.6667C2.5 17.3297 2.76339 17.9656 3.23223 18.4345C3.70107 18.9033 4.33696 19.1667 5 19.1667H15C15.663 19.1667 16.2989 18.9033 16.7678 18.4345C17.2366 17.9656 17.5 17.3297 17.5 16.6667M2.5 16.6667V10C2.5 9.33696 2.76339 8.70107 3.23223 8.23223C3.70107 7.76339 4.33696 7.5 5 7.5H15C15.663 7.5 16.2989 7.76339 16.7678 8.23223C17.2366 8.70107 17.5 9.33696 17.5 10V16.6667"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {/* {customFrom && <span className={styles.dateDisplay}>{formatDateForDisplay(customFrom)}</span>} */}
            </div>
          </div>

          <div className={styles.dateInputGroup}>
            <label className={styles.inputLabel}>To</label>
            <div className={styles.inputWrapper}>
              <input
                type="date"
                value={customTo}
                onChange={handleCustomToChange}
                className={styles.dateInput}
              />
              <svg className={styles.calendarIcon} width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M6.66667 1.66667V4.16667M13.3333 1.66667V4.16667M2.5 16.6667V6.66667C2.5 6.00363 2.76339 5.36774 3.23223 4.8989C3.70107 4.43006 4.33696 4.16667 5 4.16667H15C15.663 4.16667 16.2989 4.43006 16.7678 4.8989C17.2366 5.36774 17.5 6.00363 17.5 6.66667V16.6667M2.5 16.6667C2.5 17.3297 2.76339 17.9656 3.23223 18.4345C3.70107 18.9033 4.33696 19.1667 5 19.1667H15C15.663 19.1667 16.2989 18.9033 16.7678 18.4345C17.2366 17.9656 17.5 17.3297 17.5 16.6667M2.5 16.6667V10C2.5 9.33696 2.76339 8.70107 3.23223 8.23223C3.70107 7.76339 4.33696 7.5 5 7.5H15C15.663 7.5 16.2989 7.76339 16.7678 8.23223C17.2366 8.70107 17.5 9.33696 17.5 10V16.6667"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {/* {customTo && <span className={styles.dateDisplay}>{formatDateForDisplay(customTo)}</span>} */}
            </div>
          </div>
        </div>
      ),
    });
  }

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <div>
        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        </div>

        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        {/* Options */}
        <div className={styles.optionsContainer}>
          <ToggleButtonGroup
            options={toggleOptions}
            value={selectedOption}
            onChange={setSelectedOption}
            name="dateRange"
            direction="vertical"
          />
        </div>

        {/* Apply Button */}
        <button className={styles.applyButton} onClick={handleApply}>
          Apply
        </button>
      </div>
    </div>
  );
};

export default DateRangePicker;